import { z } from 'zod'
import { EmailSchema, IsoDateTimeSchema, PhoneSchema } from '../primitives'

/**
 * Contact verification (`/api/o/:orgSlug/contact/*`): the email (always) and the phone (required, or optional and
 * provided) are confirmed with a one-time code in OUR registration form, BEFORE the Didit identity session
 * (ID card + video selfie). Codes are sent and checked through Didit's standalone email / phone APIs; our
 * filter then rejects disposable / undeliverable emails, disposable phones and per-organization duplicates.
 *
 * A successful check returns a short-lived `proof`, bound to the organization and the normalized address, that
 * POST /join requires (`emailProof`, `phoneProof`).
 *
 * Errors (ApiErrorBody codes):
 * - 409 duplicate_email | identity_final_decision | member_removed (email), 409 duplicate_phone (phone),
 *   409 registration_closed
 * - 422 invalid_code (details.attemptsLeft) | code_expired | code_attempts_exceeded
 * - 422 email_disposable | email_undeliverable | email_rejected | invalid_email
 * - 422 phone_disposable | phone_rejected | invalid_phone
 * - 400 contact_not_verified (phone step without a valid email proof; join without the proofs)
 * - 429 resend_too_soon | rate_limited (details.retryAfterSeconds, header Retry-After)
 * - 503 contact_verification_unavailable (Didit refused the channel, e.g. phone before the first top-up)
 *   | didit_unavailable (Didit unreachable)
 */
export const CONTACT_CHANNELS = ['email', 'phone'] as const
export const ContactChannelSchema = z.enum(CONTACT_CHANNELS)
export type ContactChannel = z.infer<typeof ContactChannelSchema>

/** Minimum wait between two codes to the same address (seconds). */
export const CONTACT_RESEND_COOLDOWN_SECONDS = 60
/** Codes per address and organization per rolling hour. */
export const CONTACT_MAX_SENDS_PER_HOUR = 5
/** Lifetime of a code (Didit: 5 minutes from the first send of a verification). */
export const CONTACT_CODE_TTL_SECONDS = 5 * 60
/** Lifetime of a contact proof: /join must happen within this time after the check. */
export const CONTACT_PROOF_TTL_MINUTES = 30
/** Codes are numeric, 6 digits (Didit accepts 4–8). */
export const ContactCodeSchema = z
  .string()
  .trim()
  .regex(/^[0-9]{4,8}$/, 'Enter the code you received (digits only)')

/** Opaque proof returned by a successful check. */
export const ContactProofSchema = z.string().min(20).max(200)

/* ------------------------------------------------------------------ results stored on the member */

export const ContactEmailBreachSchema = z.object({
  name: z.string().nullable(),
  domain: z.string().nullable(),
  breachDate: z.string().nullable(),
  dataClasses: z.array(z.string()),
})
export type ContactEmailBreach = z.infer<typeof ContactEmailBreachSchema>

/** What Didit's email check reported, as stored for the organizer. */
export const ContactEmailResultSchema = z.object({
  email: z.string(),
  verifiedAt: IsoDateTimeSchema,
  /** false when Didit flags the address as undeliverable (null: not reported). */
  deliverable: z.boolean().nullable(),
  disposable: z.boolean().nullable(),
  /** Found in known data breaches (information only, never blocks). */
  breached: z.boolean().nullable(),
  breaches: z.array(ContactEmailBreachSchema),
  /** Didit warning codes (e.g. BREACHED_EMAIL_DETECTED). */
  warnings: z.array(z.string()),
  /** Didit request / session id of the verification. */
  diditRequestId: z.string().nullable(),
})
export type ContactEmailResult = z.infer<typeof ContactEmailResultSchema>

/** What Didit's phone check reported, as stored for the organizer. */
export const ContactPhoneResultSchema = z.object({
  /** E.164 */
  phone: z.string(),
  verifiedAt: IsoDateTimeSchema,
  carrier: z.string().nullable(),
  /** Didit line type: mobile, fixed_line, voip, … */
  lineType: z.string().nullable(),
  /** ISO 3166-1 alpha-2 country of the number. */
  country: z.string().nullable(),
  /** Virtual line (VoIP / ISP / VPN): allowed, but the KYC policy flags the member for review. */
  voip: z.boolean().nullable(),
  disposable: z.boolean().nullable(),
  /** Channel that delivered the code (sms, whatsapp, …). */
  channel: z.string().nullable(),
  warnings: z.array(z.string()),
  diditRequestId: z.string().nullable(),
})
export type ContactPhoneResult = z.infer<typeof ContactPhoneResultSchema>

/** Contact checks of a member (email always, phone when one was registered). */
export const ContactVerificationSchema = z.object({
  email: ContactEmailResultSchema.nullable(),
  phone: ContactPhoneResultSchema.nullable(),
})
export type ContactVerification = z.infer<typeof ContactVerificationSchema>

/* ------------------------------------------------------------------ send */

/* POST /api/o/:orgSlug/contact/email/send → ContactCodeSentResponse */
export const EmailCodeSendRequestSchema = z.object({ email: EmailSchema })
export type EmailCodeSendRequest = z.infer<typeof EmailCodeSendRequestSchema>

/* POST /api/o/:orgSlug/contact/phone/send → ContactCodeSentResponse (the email must be verified first). */
export const PhoneCodeSendRequestSchema = z.object({ phone: PhoneSchema, emailProof: ContactProofSchema })
export type PhoneCodeSendRequest = z.infer<typeof PhoneCodeSendRequestSchema>

export const ContactCodeSentResponseSchema = z.object({
  channel: ContactChannelSchema,
  /** Normalized address the code went to. */
  destination: z.string(),
  sentAt: IsoDateTimeSchema,
  /** Seconds before another code can be requested. */
  resendAfterSeconds: z.number().int().min(0),
  /** Seconds the code stays valid. */
  expiresInSeconds: z.number().int().min(0),
  /** Codes left for this address in the current hour. */
  sendsLeft: z.number().int().min(0),
})
export type ContactCodeSentResponse = z.infer<typeof ContactCodeSentResponseSchema>

/* ------------------------------------------------------------------ check */

/* POST /api/o/:orgSlug/contact/email/check → ContactCodeCheckResponse */
export const EmailCodeCheckRequestSchema = z.object({ email: EmailSchema, code: ContactCodeSchema })
export type EmailCodeCheckRequest = z.infer<typeof EmailCodeCheckRequestSchema>

/* POST /api/o/:orgSlug/contact/phone/check → ContactCodeCheckResponse */
export const PhoneCodeCheckRequestSchema = z.object({
  phone: PhoneSchema,
  code: ContactCodeSchema,
  emailProof: ContactProofSchema,
})
export type PhoneCodeCheckRequest = z.infer<typeof PhoneCodeCheckRequestSchema>

export const ContactCodeCheckResponseSchema = z.object({
  channel: ContactChannelSchema,
  destination: z.string(),
  verifiedAt: IsoDateTimeSchema,
  /** Send it to /join (emailProof / phoneProof) before `proofExpiresAt`. */
  proof: ContactProofSchema,
  proofExpiresAt: IsoDateTimeSchema,
})
export type ContactCodeCheckResponse = z.infer<typeof ContactCodeCheckResponseSchema>
